import type { CheckoutPhase, PaymentSession } from '../types';
import { TERMINAL_STATUSES } from '../types';

/** Pay countdown target — only after checkout has started. */
export function getCountdownTarget(session: PaymentSession): string | null {
  if (session.phase === 'PAYING' && session.paymentExpiryAt) {
    return session.paymentExpiryAt;
  }
  return null;
}

const PAY_PROGRESS_PHASES: CheckoutPhase[] = [
  'qr-active',
  'mobile-app-active',
  'awaiting',
  'manual-confirm',
];

const NO_PROGRESS_PHASES: CheckoutPhase[] = [
  'loading',
  'choose-method',
  'manual-entry',
  'manual-picker',
  'success',
  'expired',
  'cancelled',
  'declined',
  'failed',
  'invalid-session',
  'error',
];

/** Whether the checkout UI should show the pay-window progress bar. */
export function shouldShowPayProgress(
  session: PaymentSession | null,
  phase: CheckoutPhase,
): boolean {
  if (!session?.paymentExpiryAt || NO_PROGRESS_PHASES.includes(phase)) {
    return false;
  }

  if (TERMINAL_STATUSES.includes(session.status)) {
    return false;
  }

  if (msUntil(session.paymentExpiryAt) <= 0) {
    return false;
  }

  if (getCountdownTarget(session)) {
    return true;
  }

  return PAY_PROGRESS_PHASES.includes(phase);
}

/** Remaining pay-window ratio (1 = full time left, 0 = expired). */
export function getPayWindowRemainingRatio(session: PaymentSession): number | null {
  if (!session.paymentExpiryAt) {
    return null;
  }

  const expiresAt = session.paymentExpiryAt;
  const end = new Date(expiresAt).getTime();
  const start = session.paymentStartAt
    ? new Date(session.paymentStartAt).getTime()
    : end - 5 * 60 * 1000;
  const total = end - start;

  if (total <= 0) {
    return 0;
  }

  return Math.min(1, Math.max(0, msUntil(expiresAt) / total));
}

/** Idle shelf deadline — show soft hint only, not a pay countdown. */
export function getSoftIdleHint(session: PaymentSession): string | null {
  if (session.phase === 'IDLE' && session.expiresAt) {
    return session.expiresAt;
  }
  return null;
}

export function msUntil(iso: string): number {
  return Math.max(0, new Date(iso).getTime() - Date.now());
}
